import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import lottoApi from "../api";
import { useAuth } from "../../contexts/JWTContext";
import { useAsync, pushToast } from "../lib";
import { Skeleton, LoginGate } from "../ui";
import { polar, slicePath, rotationForIndex } from "../wheelMath";
import { DIAMOND_CHANGED, DIAMOND_OPEN, useDiamondBoxEnabled } from "../diamondLogic";

const MAX_PRIZES = 10;

// พาเลตต์นีออนคาสิโน: ช่องวงล้อเป็นสีอัญมณีไล่เฉด วนซ้ำทุก 5 ช่อง และช่องรางวัลใหญ่สุดเป็นทองประกาย
const HUES = [
  { id: "v", a: "#4c1d95", b: "#a78bfa" },
  { id: "p", a: "#9d174d", b: "#f472b6" },
  { id: "c", a: "#0e7490", b: "#22d3ee" },
  { id: "g", a: "#047857", b: "#34d399" },
  { id: "b", a: "#1d4ed8", b: "#60a5fa" },
  { id: "x", a: "#b45309", b: "#fde047" },
];
const TOP_HUE = 5;
const hueOf = (i, isTop) => HUES[isTop ? TOP_HUE : i % 5];

const VB = 400;
const C = VB / 2;
const LEDS = 24;

const IC = ({ name, ...props }) => <Icon icon={`fluent-emoji-flat:${name}`} {...props} />;

function labelSize(text) {
  const n = String(text).length;
  if (n <= 2) return 26;
  if (n <= 4) return 22;
  if (n <= 5) return 19;
  if (n <= 6) return 16;
  return 13;
}

function WheelDefs() {
  return (
    <defs>
      {HUES.map((h) => (
        <radialGradient key={h.id} id={`nw-g-${h.id}`} cx={C} cy={C} r="172" gradientUnits="userSpaceOnUse">
          <stop offset="0.12" stopColor={h.a} />
          <stop offset="1" stopColor={h.b} />
        </radialGradient>
      ))}
      <linearGradient id="nw-ring" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#2a1a5e" />
        <stop offset="1" stopColor="#0d0824" />
      </linearGradient>
      <radialGradient id="nw-glass" cx="0.3" cy="0.2" r="0.9">
        <stop offset="0" stopColor="#fff" stopOpacity="0.32" />
        <stop offset="0.45" stopColor="#fff" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="nw-pin" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#ffe3f2" />
        <stop offset="0.35" stopColor="#f472b6" />
        <stop offset="1" stopColor="#be185d" />
      </linearGradient>
    </defs>
  );
}

const SPARKS = Array.from({ length: 24 }, (_, i) => ({
  left: (i * 41) % 100,
  delay: ((i * 57) % 100) / 100,
  dur: 1.7 + ((i * 31) % 14) / 10,
  hue: ["#22d3ee", "#f472b6", "#fde047", "#a78bfa", "#34d399", "#fff"][i % 6],
  rot: (i * 53) % 360,
  round: i % 3 === 0,
}));

export default function WheelSpinPage() {
  const { user, isAuthenticated } = useAuth();
  const boxEnabled = useDiamondBoxEnabled();
  const nav = useNavigate();
  const uid = user?.id;

  const { data: settings, loading: loadingSettings } = useAsync(() => lottoApi.wheelSettings(), []);
  const { data: pointRaw, reload: reloadPoint } = useAsync(
    () => (uid ? lottoApi.wheelPointOf(uid) : Promise.resolve(0)),
    [uid],
  );
  const point = Number(pointRaw) || 0;

  const [deg, setDeg] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [lastIdx, setLastIdx] = useState(-1);

  const prizes = useMemo(
    () => Array.from({ length: MAX_PRIZES }, (_, i) => Number(settings?.[`prizeAmount${i + 1}`]) || 0),
    [settings],
  );
  const probabilities = useMemo(
    () => Array.from({ length: MAX_PRIZES }, (_, i) => (Number(settings?.[`prizeProb${i + 1}`]) || 0) / 100),
    [settings],
  );
  const prizeTypes = useMemo(
    () => Array.from({ length: MAX_PRIZES }, (_, i) => settings?.[`prizeType${i + 1}`] ?? "credit"),
    [settings],
  );
  const prizeLabels = useMemo(
    () => Array.from({ length: MAX_PRIZES }, (_, i) => settings?.[`prizeLabel${i + 1}`] ?? ""),
    [settings],
  );
  const spinCost = Number(settings?.spinAmount ?? settings?.spinCost ?? 0);
  const sectorAngle = 360 / prizes.length;

  const topIndex = useMemo(() => {
    let best = -1;
    prizes.forEach((p, i) => {
      if (prizeTypes[i] !== "item" && p > 0 && (best < 0 || p > prizes[best])) best = i;
    });
    return best;
  }, [prizes, prizeTypes]);

  const canSpin =
    isAuthenticated &&
    !spinning &&
    !!settings &&
    point >= spinCost &&
    probabilities.some((p) => p > 0);

  const spin = async () => {
    if (!canSpin) return;
    setSpinning(true);
    setLastIdx(-1);
    try {
      const payload = await lottoApi.wheelSpin(uid);
      const winningIndex = Number(payload?.winningIndex);
      const idx = Number.isInteger(winningIndex) ? winningIndex : -1;
      if (idx < 0 || idx >= prizes.length) throw new Error("ผลการหมุนไม่ถูกต้อง");

      const rotation = rotationForIndex(idx, prizes.length, deg);
      setDeg((d) => d + rotation);

      const prizeAmount = Number(payload?.prize);
      const hasPrize = typeof payload?.hasPrize === "boolean" ? payload.hasPrize : prizeAmount > 0;
      const amount = Number.isNaN(prizeAmount) ? prizes[idx] || 0 : prizeAmount;
      const turnNeed = Number(payload?.turnover_required) || 0;
      const message =
        (payload?.message || (hasPrize ? `ยินดีด้วย! คุณได้รับ ${amount}฿` : "เสียใจด้วย คุณไม่ได้รางวัล")) +
        (hasPrize && turnNeed > 0 ? ` (ติดเทิร์น ฿${turnNeed.toLocaleString("th-TH")} แทงให้ครบก่อนถอน)` : "");

      setTimeout(() => {
        setLastIdx(idx);
        setResult({ hasPrize, amount, message, label: prizeTypes[idx] === "item" ? prizeLabels[idx] : "" });
        reloadPoint();
        setSpinning(false);
      }, 5200);
    } catch (e) {
      pushToast(e?.response?.data?.message || e?.message || "เกิดข้อผิดพลาดในการหมุนวงล้อ", "err", 6000);
      setSpinning(false);
    }
  };

  useEffect(() => {
    if (uid) reloadPoint();
  }, [uid, reloadPoint]);

  // รับเพชรจากกล่องลอย → แต้มบนหน้านี้อัปเดตทันที
  useEffect(() => {
    window.addEventListener(DIAMOND_CHANGED, reloadPoint);
    return () => window.removeEventListener(DIAMOND_CHANGED, reloadPoint);
  }, [reloadPoint]);

  useEffect(() => {
    if (!result) return undefined;
    const onKey = (e) => e.key === "Escape" && setResult(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [result]);

  if (!isAuthenticated) {
    return (
      <LoginGate
        icon="fluent-emoji-flat:circus-tent"
        title="เข้าสู่ระบบก่อนหมุนวงล้อ"
        desc="สะสมแต้มแล้วมาลุ้นรางวัลกับกงล้อพารวย"
        perks={["สะสมแต้มเพชร","หมุนลุ้นรางวัล","ลุ้นรับเครดิตฟรี"]}
        onLogin={() => nav("/auth")}
      />
    );
  }

  if (loadingSettings) return <Skeleton h={420} style={{ marginTop: 18 }} />;

  const spinsLeft = spinCost > 0 ? Math.floor(point / spinCost) : null;
  const progress = spinCost > 0 ? Math.min(100, (point / spinCost) * 100) : 100;
  const labelOf = (i) => (prizeTypes[i] === "item" ? (prizeLabels[i] || "🎁").slice(0, 10) : prizes[i] ? `${prizes[i]}฿` : "-");
  const hint = spinning
    ? "กำลังหมุน... ลุ้นสุดตัว!"
    : canSpin
      ? "พร้อมแล้ว กดหมุนได้เลย"
      : point < spinCost
        ? "แต้มเพชรไม่พอสำหรับการหมุน"
        : "ยังไม่พร้อมให้หมุนในขณะนี้";

  return (
    <div className="nw-page" style={{ marginTop: 18 }}>
      <header className="nw-hero">
        <IC name="sparkles" width={30} className="nw-hero-sp l" aria-hidden="true" />
        <IC name="sparkles" width={22} className="nw-hero-sp r" aria-hidden="true" />
        <div className="nw-kicker">LUCKY WHEEL</div>
        <h1 className="nw-title">กงล้อพารวย</h1>
        <p className="nw-sub">สะสมแต้มเพชรแล้วมาลุ้นของรางวัล</p>
      </header>

      <div className="nw-layout">
        <section className={`nw-stage${spinning ? " is-spinning" : ""}`}>
          <div className="nw-wheel-wrap">
            <div className="nw-halo" aria-hidden="true" />
            <svg className="nw-rim" viewBox={`0 0 ${VB} ${VB}`} aria-hidden="true">
              <WheelDefs />
              <circle cx={C} cy={C} r="198" fill="url(#nw-ring)" stroke="rgba(255,255,255,.35)" strokeWidth="1.5" />
              <circle cx={C} cy={C} r="179" fill="none" stroke="rgba(255,255,255,.22)" strokeWidth="1" />
              {Array.from({ length: LEDS }, (_, i) => {
                const [x, y] = polar(C, C, 188.5, (i * 360) / LEDS);
                return <circle key={i} className={`nw-led ${i % 2 ? "a" : "b"}`} cx={x} cy={y} r="3.6" />;
              })}
            </svg>

            <svg
              className="nw-disc"
              viewBox={`0 0 ${VB} ${VB}`}
              style={{ transform: `rotate(${deg}deg)` }}
              role="img"
              aria-label="วงล้อรางวัล"
            >
              <WheelDefs />
              {prizes.map((_, i) => {
                const isTop = i === topIndex;
                const hue = hueOf(i, isTop);
                const text = labelOf(i);
                return (
                  <g key={i}>
                    <path
                      d={slicePath(C, C, 172, i * sectorAngle, (i + 1) * sectorAngle)}
                      fill={`url(#nw-g-${hue.id})`}
                      stroke="rgba(255,255,255,.55)"
                      strokeWidth="1.4"
                    />
                    <g transform={`rotate(${i * sectorAngle + sectorAngle / 2} ${C} ${C})`}>
                      <text
                        x={C}
                        y={C - 128}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={labelSize(text)}
                        fill={isTop ? "#3b1d00" : "#fff"}
                        className={`nw-label${isTop ? " top" : ""}`}
                      >
                        {text}
                      </text>
                      <circle cx={C} cy={C - 92} r={isTop ? 4.5 : 3} fill="#fff" fillOpacity={isTop ? 0.95 : 0.55} />
                    </g>
                  </g>
                );
              })}
              <circle cx={C} cy={C} r="172" fill="none" stroke="rgba(255,255,255,.8)" strokeWidth="2.5" />
              <circle cx={C} cy={C} r="172" fill="url(#nw-glass)" />
            </svg>

            <svg className="nw-pointer" viewBox="0 0 40 50" aria-hidden="true">
              <path d="M5 5h30L20 45Z" fill="url(#nw-pin)" stroke="#fff" strokeWidth="2.4" strokeLinejoin="round" />
            </svg>

            <button type="button" className="nw-hub" onClick={spin} disabled={!canSpin} tabIndex={-1} aria-hidden="true">
              <IC name="gem-stone" aria-hidden="true" />
            </button>
          </div>

          <button type="button" className="nw-spin" onClick={spin} disabled={!canSpin}>
            <Icon icon="solar:refresh-circle-bold-duotone" width={24} className="nw-spin-ic" />
            <span>{spinning ? "กำลังหมุน..." : "หมุนเลย"}</span>
            {!spinning && spinCost > 0 && <em>{spinCost.toLocaleString()} แต้ม</em>}
          </button>
          <p className="nw-hint">{hint}</p>
        </section>

        <aside className="nw-side">
          <div className="nw-bal">
            <IC name="gem-stone" width={44} className="nw-bal-gem" aria-hidden="true" />
            <div className="nw-bal-body">
              <div className="nw-bal-lbl">แต้มเพชรของฉัน</div>
              <div className="nw-bal-num">{point.toLocaleString()}</div>
            </div>
            {spinsLeft !== null && <div className="nw-chip">หมุนได้อีก {spinsLeft.toLocaleString()} ครั้ง</div>}
            {boxEnabled && (
              <button
                type="button"
                className="nw-histbtn"
                onClick={() => window.dispatchEvent(new CustomEvent(DIAMOND_OPEN, { detail: { tab: "history" } }))}
              >
                <Icon icon="solar:history-bold-duotone" width={16} /> ประวัติแต้มเพชร
              </button>
            )}
            <div className="nw-bar" aria-hidden="true">
              <i style={{ width: `${progress}%` }} />
            </div>
            <div className="nw-bal-cost">
              <IC name="ticket" width={18} aria-hidden="true" />
              ใช้ {spinCost.toLocaleString()} แต้มต่อการหมุน 1 ครั้ง
            </div>
          </div>

          <div className="nw-prizes">
            <div className="nw-prizes-h">
              <IC name="trophy" width={20} aria-hidden="true" /> รางวัลบนวงล้อ
            </div>
            <ul>
              {prizes.map((_, i) => {
                const isTop = i === topIndex;
                const hue = hueOf(i, isTop);
                return (
                  <li key={i} className={`${isTop ? "top " : ""}${lastIdx === i ? "hit" : ""}`}>
                    <span className="nw-dot" style={{ background: `linear-gradient(135deg, ${hue.a}, ${hue.b})` }} />
                    <span className="nw-pz">{labelOf(i)}</span>
                    {isTop && <IC name="crown" width={16} aria-hidden="true" />}
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>
      </div>

      {result && (
        <div className="nw-modal" onClick={() => setResult(null)}>
          <div
            className={`nw-modal-card ${result.hasPrize ? "win" : "lose"}`}
            role="dialog"
            aria-modal="true"
            aria-label="ผลการหมุน"
            onClick={(e) => e.stopPropagation()}
          >
            {result.hasPrize && <div className="nw-rays" aria-hidden="true" />}
            {result.hasPrize &&
              SPARKS.map((c, i) => (
                <i
                  key={i}
                  className={`nw-spark${c.round ? " round" : ""}`}
                  style={{ left: `${c.left}%`, background: c.hue, animationDelay: `${c.delay}s`, animationDuration: `${c.dur}s`, "--r": `${c.rot}deg` }}
                />
              ))}
            <IC name={result.hasPrize ? "trophy" : "crystal-ball"} width={72} aria-hidden="true" />
            <div className="nw-modal-title">{result.hasPrize ? "แจ็กพอต! ยินดีด้วย" : "เสียดายจัง"}</div>
            {result.hasPrize && <div className="nw-modal-prize">{result.label || `${result.amount.toLocaleString()}฿`}</div>}
            <p className="nw-modal-msg">{result.message}</p>
            <button type="button" className="nw-modal-btn" onClick={() => setResult(null)} autoFocus>
              ตกลง
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
